import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePaymentSummaryQuery } from "@/modules/payments/presentation/api/queries";
import {
  useCustomerGroupStatsQuery,
  useSalesByChannelQuery,
  useSalesByCustomerGroupQuery,
  useSalesBySocialSourceQuery,
  useSalesTimeseriesQuery,
} from "@/modules/reports/presentation/api/queries";
import { AreaChart } from "@/shared/ui/AreaChart";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { PieChart } from "@/shared/ui/PieChart";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: "ລໍຊຳລະ",
  paid: "ຊຳລະແລ້ວ",
  failed: "ລົ້ມເຫຼວ",
  partially_refunded: "ຄືນບາງສ່ວນ",
  refunded: "ຄືນເງິນ",
  cancelled: "ຍົກເລີກ",
};

const ORDER_CHANNEL_LABELS: Record<string, string> = {
  web: "ເວັບ",
  retail: "ຫນ້າຮ້ານ",
  social: "Social Media",
};

const PAYMENT_STATUS_CHART_CONFIG: ChartConfig = {
  pending: { label: PAYMENT_STATUS_LABELS.pending, color: "var(--chart-1)" },
  paid: { label: PAYMENT_STATUS_LABELS.paid, color: "var(--chart-2)" },
  failed: { label: PAYMENT_STATUS_LABELS.failed, color: "var(--chart-3)" },
  partially_refunded: {
    label: PAYMENT_STATUS_LABELS.partially_refunded,
    color: "var(--chart-4)",
  },
  refunded: { label: PAYMENT_STATUS_LABELS.refunded, color: "var(--chart-5)" },
  cancelled: {
    label: PAYMENT_STATUS_LABELS.cancelled,
    color: "hsl(var(--muted-foreground))",
  },
};

const CHANNEL_CHART_CONFIG: ChartConfig = {
  web: { label: ORDER_CHANNEL_LABELS.web, color: "var(--chart-1)" },
  retail: { label: ORDER_CHANNEL_LABELS.retail, color: "var(--chart-2)" },
  social: { label: ORDER_CHANNEL_LABELS.social, color: "var(--chart-3)" },
};

const SOCIAL_SLICE_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
] as const;

function computeReportRange(preset: "7d" | "30d" | "month"): {
  from: string;
  to: string;
} {
  const to = new Date();
  const toIso = to.toISOString();
  if (preset === "month") {
    const from = new Date(to.getFullYear(), to.getMonth(), 1, 0, 0, 0, 0);
    return { from: from.toISOString(), to: toIso };
  }
  const from = new Date(to);
  const days = preset === "7d" ? 7 : 30;
  from.setTime(from.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: toIso };
}

export function ReportsChartsPage() {
  const paymentSummary = usePaymentSummaryQuery();

  const [reportPreset, setReportPreset] = useState<"7d" | "30d" | "month">(
    "30d",
  );
  const [reportGranularity, setReportGranularity] = useState<"day" | "month">(
    "day",
  );

  const reportRange = useMemo(
    () => computeReportRange(reportPreset),
    [reportPreset],
  );

  const salesTimeseries = useSalesTimeseriesQuery({
    from: reportRange.from,
    to: reportRange.to,
    granularity: reportGranularity,
  });

  const salesByChannel = useSalesByChannelQuery({
    from: reportRange.from,
    to: reportRange.to,
  });

  const salesBySocialSource = useSalesBySocialSourceQuery({
    from: reportRange.from,
    to: reportRange.to,
  });

  const salesByCustomerGroup = useSalesByCustomerGroupQuery({
    from: reportRange.from,
    to: reportRange.to,
  });

  const customerGroupStats = useCustomerGroupStatsQuery();

  const timeseriesChartData = useMemo(
    () =>
      (salesTimeseries.data ?? []).map((row) => ({
        bucketLabel: row.bucketLabel,
        revenue: row.revenue,
      })),
    [salesTimeseries.data],
  );

  const timeseriesChartConfig: ChartConfig = useMemo(
    () => ({
      revenue: {
        label: "ຍອດເງິນທີ່ໄດ້ຮັບ (ກີບ)",
        color: "var(--chart-1)",
      },
    }),
    [],
  );

  const channelPieData = useMemo(
    () =>
      (salesByChannel.data ?? []).map((row) => ({
        name: row.channel,
        value: row.revenue,
      })),
    [salesByChannel.data],
  );

  const socialPieData = useMemo(
    () =>
      (salesBySocialSource.data ?? []).map((row) => ({
        name: row.code,
        value: row.revenue,
      })),
    [salesBySocialSource.data],
  );

  const socialPieConfig: ChartConfig = useMemo(() => {
    const cfg: ChartConfig = {};
    (salesBySocialSource.data ?? []).forEach((row, i) => {
      cfg[row.code] = {
        label: row.name,
        color: SOCIAL_SLICE_COLORS[i % SOCIAL_SLICE_COLORS.length],
      };
    });
    return cfg;
  }, [salesBySocialSource.data]);

  const groupRevenuePieData = useMemo(
    () =>
      (salesByCustomerGroup.data ?? []).map((row) => ({
        name: row.groupId,
        value: row.revenue,
      })),
    [salesByCustomerGroup.data],
  );

  const groupRevenuePieConfig: ChartConfig = useMemo(() => {
    const cfg: ChartConfig = {};
    (salesByCustomerGroup.data ?? []).forEach((row, i) => {
      cfg[row.groupId] = {
        label: row.groupName,
        color: row.color ?? SOCIAL_SLICE_COLORS[i % SOCIAL_SLICE_COLORS.length],
      };
    });
    return cfg;
  }, [salesByCustomerGroup.data]);

  const groupStatsData = customerGroupStats.data ?? [];

  const paymentPieData = useMemo(() => {
    if (!paymentSummary.data?.byStatus) return [];
    return Object.entries(paymentSummary.data.byStatus)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name, value }));
  }, [paymentSummary.data?.byStatus]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ລາຍງານກຣາຟຊຳລະ" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl tracking-tight">
              ລາຍງານກຣາຟຊຳລະ
            </h1>
            <p className="mt-1 max-w-2xl text-muted-foreground text-sm leading-relaxed">
              ເບິ່ງແນວໂນ້ມເງິນທີ່ໄດ້ຮັບຈາກການຊຳລະທີ່ສຳເລັດແລ້ວ, ເບິ່ງວ່າຍອດມາຈາກເວັບ / ຮ້ານ /
              Social Mediaເທົ່າໃດ — ພ້ອມແບ່ງຍອດພາຍໃນ Social ຕາມແພລດຟອມ (Facebook, LINE, …) —
              ແລະ ປຽບທຽບຈຳນວນລາຍການຕາມສະຖານະຊຳລະໃນລະບົບ.
            </p>
          </div>
          <Link to="/app/payments">
            <Button variant="outline" size="sm">
              ເບິ່ງການຊຳລະທັງໝົດ
            </Button>
          </Link>
        </div>

        <section className="space-y-6">
          <div className="rounded-lg border border-dashed bg-muted/20 px-3 py-2 text-muted-foreground text-xs leading-relaxed">
            <span className="font-medium text-foreground">ໝາຍເຫດ:</span> ກຣາຟ{" "}
            <strong className="text-foreground">ເສັ້ນຍ່ອຍ</strong>,{" "}
            <strong className="text-foreground">ແຜນວົງຊ່ອງທາງ</strong> ແລະ{" "}
            <strong className="text-foreground">ແຜນວົງແພລດຟອມ Social</strong> ຈະກອງຕາມ
            ຊ່ວງເວລາທີ່ເລືອກດ້ານເທິງ. ກຣາຟ{" "}
            <strong className="text-foreground">ສະຖານະຊຳລະ</strong> ສະແດງທັງລະບົບ
            ບໍ່ໄດ້ກອງຕາມຊ່ວງເວລາທີ່ເລືອກ — ໃຊ້ເພື່ອເຫັນພາບລວມວ່າມີລາຍການຄ້າງ / ສຳເລັດ / ຄືນເງິນ
            ປະມານເທົ່າໃດ.
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <p className="font-medium text-foreground text-xs">
                ຊ່ວງເວລາຂອງກຣາຟ
              </p>
              <p className="text-[11px] text-muted-foreground leading-snug">
                ໃຊ້ກັບກຣາຟເສັ້ນຍ່ອຍ, ແຜນວົງຊ່ອງທາງ ແລະ ແຜນວົງແພລດຟອມ Social
              </p>
              <Select
                value={reportPreset}
                onValueChange={(v) =>
                  setReportPreset(v as "7d" | "30d" | "month")
                }
              >
                <SelectTrigger className="h-9 w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">7 ວັນຜ່ານມາ</SelectItem>
                  <SelectItem value="30d">30 ວັນຜ່ານມາ</SelectItem>
                  <SelectItem value="month">ເດືອນນີ້</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <p className="font-medium text-foreground text-xs">
                ຮວບຮ່ອມຕາມວັນ ຫຼື ຕາມເດືອນ
              </p>
              <p className="text-[11px] text-muted-foreground leading-snug">
                ໃຊ້ກັບກຣາຟເສັ້ນຍ່ອຍເທົ່ານັ້ນ — ລວມຍອດເປັນວັນ ຫຼື ລວມເປັນເດືອນ
              </p>
              <Select
                value={reportGranularity}
                onValueChange={(v) =>
                  setReportGranularity(v as "day" | "month")
                }
              >
                <SelectTrigger className="h-9 w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">ຕາມວັນ</SelectItem>
                  <SelectItem value="month">ຕາມເດືອນ</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-xl border bg-card p-4">
            <h2 className="font-semibold text-base">ເງິນເຂົ້າຕາມວັນທີ່ຊຳລະສຳເລັດ</h2>
            <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
              ສະແດງຍອດເງິນທີ່ໄດ້ຮັບຈາກລາຍການທີ່ສະຖານະ{" "}
              <span className="text-foreground">ຊຳລະແລ້ວ</span> ເທົ່ານັ້ນ —
              ບໍ່ນັບລາຍການທີ່ຄ້າງຈ່າຍ ຫຼື ຍົກເລີກ. ແຕ່ລະຈຸດໃນກຣາຟແມ່ນຕາມວັນທີ່ລະບົບບັນທຶກວ່າຊຳລະສຳເລັດ
              (ຖ້າບໍ່ມີວັນທີ່ຊັດເຈນ ຈະໃຊ້ເວລາອັບເດດລ່າສຸດແທນ).
            </p>
            {salesTimeseries.isPending ? (
              <p className="mt-6 text-muted-foreground text-sm">ກຳລັງໂຫຼດ…</p>
            ) : salesTimeseries.isError ? (
              <p className="mt-6 text-destructive text-sm">
                {salesTimeseries.error instanceof Error
                  ? salesTimeseries.error.message
                  : "ໂຫຼດບໍ່ສຳເລັດ"}
              </p>
            ) : timeseriesChartData.length === 0 ? (
              <p className="mt-6 text-muted-foreground text-sm">
                ບໍ່ມີຂໍ້ມູນຊຳລະສຳເລັດໃນຊ່ວງນີ້ — ລອງຂະຫຍາຍຊ່ວງເວລາ ຫຼື
                ກວດກາວ່າມີລາຍການທີ່ຊຳລະແລ້ວຫຼືບໍ່
              </p>
            ) : (
              <div className="mt-4 w-full">
                <AreaChart
                  chartHeight={280}
                  data={timeseriesChartData}
                  config={timeseriesChartConfig}
                  xDataKey="bucketLabel"
                  valueDataKey="revenue"
                  xAxisTickFormatter={(v) =>
                    reportGranularity === "month"
                      ? String(v).slice(0, 7)
                      : String(v).slice(5)
                  }
                />
              </div>
            )}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-xl border bg-card p-4">
              <h2 className="font-semibold text-base">
                ຍອດເງິນຕາມຊ່ອງທາງຂາຍຂອງຄຳສັ່ງຊື້
              </h2>
              <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
                ແບ່ງຕາມວ່າອໍເດີ້ມາຈາກ <span className="text-foreground">ເວັບ</span>,{" "}
                <span className="text-foreground">ຂາຍຫນ້າຮ້ານ</span>, ຫຼື{" "}
                <span className="text-foreground">Social Media</span>.
                ນັບເຉພາະເງິນຈາກການຊຳລະທີ່ສຳເລັດແລ້ວ ພາຍໃນຊ່ວງເວລາທີ່ເລືອກໄວ້ຂ້າງເທິງ —
                ເຫັນວ່າຊ່ອງທາງໃດສ້າງລາຍໄດ້ຫຼາຍສຸດໃນຊ່ວງນັ້ນ.
              </p>
              {salesByChannel.isPending ? (
                <p className="mt-6 text-muted-foreground text-sm">ກຳລັງໂຫຼດ…</p>
              ) : salesByChannel.isError ? (
                <p className="mt-6 text-destructive text-sm">
                  {salesByChannel.error instanceof Error
                    ? salesByChannel.error.message
                    : "ໂຫຼດບໍ່ສຳເລັດ"}
                </p>
              ) : channelPieData.length === 0 ? (
                <p className="mt-6 text-muted-foreground text-sm">
                  ບໍ່ມີຂໍ້ມູນຕາມຊ່ອງທາງໃນຊ່ວງນີ້ — ອາດຍັງບໍ່ມີການຊຳລະສຳເລັດ ຫຼື ບໍ່ມີອໍເດີ້ໃນຊ່ອງນີ້
                </p>
              ) : (
                <div className="mx-auto mt-4 w-full max-w-[320px]">
                  <PieChart
                    chartHeight={280}
                    data={channelPieData}
                    config={CHANNEL_CHART_CONFIG}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={48}
                    outerRadius={88}
                  />
                </div>
              )}
            </div>

            <div className="rounded-xl border bg-card p-4">
              <h2 className="font-semibold text-base">
                ຈຳນວນລາຍການຊຳລະຕາມສະຖານະ (ທັງລະບົບ)
              </h2>
              <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
                ນັບເປັນຈຳນວນຄັ້ງຂອງແຕ່ລະສະຖານະ ທັງໝົດໃນຖານຂໍ້ມູນ —
                ບໍ່ກອງຕາມຊ່ວງວັນທີ່ທີ່ເລືອກຂ້າງເທິງ. ເຫມາະເພື່ອເບິ່ງພາບລວມວ່າມີລາຍການຄ້າງຊຳລະ,
                ສຳເລັດແລ້ວ, ລົ້ມເຫຼວ, ຄືນເງິນ ປະມານເທົ່າໃດທັງໝົດ.
              </p>
              {paymentSummary.isPending ? (
                <p className="mt-6 text-muted-foreground text-sm">ກຳລັງໂຫຼດ…</p>
              ) : paymentPieData.length === 0 ? (
                <p className="mt-6 text-muted-foreground text-sm">
                  ບໍ່ມີຂໍ້ມູນສຳລັບກຣາຟນີ້ — ອາດຍັງບໍ່ມີລາຍການຊຳລະໃນລະບົບ ຫຼື ທຸກສະຖານະມີຈຳນວນເປັນ 0
                </p>
              ) : (
                <div className="mx-auto mt-4 w-full max-w-[320px]">
                  <PieChart
                    chartHeight={280}
                    data={paymentPieData}
                    config={PAYMENT_STATUS_CHART_CONFIG}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={48}
                    outerRadius={88}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 rounded-xl border bg-card p-4">
            <h2 className="font-semibold text-base">
              ຍອດເງິນຕາມແພລດຟອມ (ອໍເດີ້ Social Media)
            </h2>
            <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
              ນັບເຉພາະການຊຳລະທີ່ສະຖານະ{" "}
              <span className="text-foreground">ຊຳລະແລ້ວ</span> ຂອງຄຳສັ່ງຊື້ທີ່ຊ່ອງທາງເປັນ{" "}
              <span className="text-foreground">Social Media</span> ພາຍໃນຊ່ວງເວລາທີ່ເລືອກ —
              ແບ່ງຕາມແຫຼ່ງຂາຍທີ່ບັນທຶກໃນອໍເດີ້ (Facebook, LINE, …). ອໍເດີ້ເກົ່າທີ່ບໍ່ມີແຫຼ່ງຈະລວມເປັນ «ບໍ່ໄດ້ລະບຸ».
            </p>
            {salesBySocialSource.isPending ? (
              <p className="mt-6 text-muted-foreground text-sm">ກຳລັງໂຫຼດ…</p>
            ) : salesBySocialSource.isError ? (
              <p className="mt-6 text-destructive text-sm">
                {salesBySocialSource.error instanceof Error
                  ? salesBySocialSource.error.message
                  : "ໂຫຼດບໍ່ສຳເລັດ"}
              </p>
            ) : socialPieData.length === 0 ? (
              <p className="mt-6 text-muted-foreground text-sm">
                ບໍ່ມີຍອດຊຳລະສຳເລັດຈາກອໍເດີ້ Social ໃນຊ່ວງນີ້ — ຫຼື ຍັງບໍ່ມີການຊຳລະແລ້ວສຳລັບຊ່ອງນີ້
              </p>
            ) : (
              <div className="mx-auto mt-4 w-full max-w-[360px]">
                <PieChart
                  chartHeight={300}
                  data={socialPieData}
                  config={socialPieConfig}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={52}
                  outerRadius={96}
                />
              </div>
            )}
          </div>

          {/* Customer Group Charts */}
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div className="rounded-xl border bg-card p-4">
              <h2 className="font-semibold text-base">
                ຍອດຂາຍຕາມກຸ່ມລູກຄ້າ
              </h2>
              <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
                ນັບຍອດຊຳລະສຳເລັດ ແຍກຕາມກຸ່ມທີ່ລູກຄ້າຂອງອໍເດີ້ສັງກັດ —
                ລູກຄ້າທີ່ຢູ່ຫຼາຍກຸ່ມຈະຖືກນັບທຸກກຸ່ມ.
                ອໍເດີ້ທີ່ບໍ່ມີລູກຄ້າ ຫຼື ລູກຄ້າບໍ່ຢູ່ກຸ່ມໃດຈະບໍ່ຖືກນັບ.
              </p>
              {salesByCustomerGroup.isPending ? (
                <p className="mt-6 text-muted-foreground text-sm">ກຳລັງໂຫຼດ…</p>
              ) : salesByCustomerGroup.isError ? (
                <p className="mt-6 text-destructive text-sm">
                  {salesByCustomerGroup.error instanceof Error
                    ? salesByCustomerGroup.error.message
                    : "ໂຫຼດບໍ່ສຳເລັດ"}
                </p>
              ) : groupRevenuePieData.length === 0 ? (
                <p className="mt-6 text-muted-foreground text-sm">
                  ບໍ່ມີຂໍ້ມູນ — ອາດຍັງບໍ່ໄດ້ຈັດກຸ່ມລູກຄ້າ ຫຼື ບໍ່ມີການຊຳລະໃນຊ່ວງນີ້
                </p>
              ) : (
                <>
                  <div className="mx-auto mt-4 w-full max-w-[320px]">
                    <PieChart
                      chartHeight={280}
                      data={groupRevenuePieData}
                      config={groupRevenuePieConfig}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={48}
                      outerRadius={88}
                    />
                  </div>
                  <div className="mt-3 space-y-1">
                    {(salesByCustomerGroup.data ?? []).map((row) => (
                      <div
                        key={row.groupId}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="flex items-center gap-2">
                          <span
                            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{
                              backgroundColor:
                                row.color ?? "var(--chart-1)",
                            }}
                          />
                          <span className="text-muted-foreground">
                            {row.groupName}
                          </span>
                        </span>
                        <span className="font-medium tabular-nums">
                          {row.orderCount} ຄຳສັ່ງ
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="rounded-xl border bg-card p-4">
              <h2 className="font-semibold text-base">
                ຈຳນວນລູກຄ້າຕໍ່ກຸ່ມ (ທັງລະບົບ)
              </h2>
              <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
                ສະແດງຈຳນວນລູກຄ້າທີ່ສັງກັດແຕ່ລະກຸ່ມໃນຖານຂໍ້ມູນທັງໝົດ —
                ບໍ່ຂຶ້ນກັບຊ່ວງເວລາທີ່ເລືອກ.
                ເຫມາະສຳລັບເຫັນ distribution ຂອງຖານລູກຄ້າ.
              </p>
              {customerGroupStats.isPending ? (
                <p className="mt-6 text-muted-foreground text-sm">ກຳລັງໂຫຼດ…</p>
              ) : groupStatsData.length === 0 ? (
                <p className="mt-6 text-muted-foreground text-sm">
                  ຍັງບໍ່ມີກຸ່ມລູກຄ້າ — ສ້າງກຸ່ມກ່ອນໃນໜ້ານ <strong>ກຸ່ມລູກຄ້າ</strong>
                </p>
              ) : (
                <div className="mt-4 space-y-2">
                  {groupStatsData.map((row) => {
                    const max = Math.max(
                      ...groupStatsData.map((r) => r.memberCount),
                      1,
                    );
                    const pct = Math.round((row.memberCount / max) * 100);
                    return (
                      <div key={row.groupId} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <span
                              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border"
                              style={{
                                backgroundColor: row.color
                                  ? `${row.color}33`
                                  : "transparent",
                                borderColor:
                                  row.color ?? "var(--border)",
                              }}
                            />
                            {row.groupName}
                          </span>
                          <span className="font-medium tabular-nums text-muted-foreground">
                            {row.memberCount} ຄົນ
                          </span>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${pct}%`,
                              backgroundColor:
                                row.color ?? "var(--chart-1)",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>
      </Main>
    </>
  );
}
